import styled from 'styled-components';

import { useEffect, useRef, useState } from 'react';

import { AppDispatch, State } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@common/Button';
import { Pill, Title } from './Login';
import { refresh } from '@store/async';
import { useLocation, useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  background: black;
`;

const Body = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  background: ${({ theme }) => theme.background.standard};
  font-family: 'Space Grotesk';
  position: relative;
  z-index: 2;

  padding-top: 10vh;
  min-width: 500px;
  min-height: 600px;
  height: 100vh;
  width: 100vw;

  a {
    color: ${({ theme }) => theme.primary};
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 400px;
  margin-top: 1rem;
  gap: 1rem;

  & .MuiAlert-root {
    border: 0;
  }
`;

export const InternalLogin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const titleRef: any = useRef();
  const navigate = useNavigate();

  const loginLoading = useSelector((state: State) => state.auth.loginLoading);
  const isLocalUser = useSelector((state: State) => state.auth.isLocalUser);
  const invalidPermission = useSelector((state: State) => state.auth.invalidPermission);
  const autoLogin = useSelector((state: State) => state.auth.autoLogin);
  const isLoggedIn = useSelector((state: State) => state.auth.isLoggedIn);
  const logoutLoading = useSelector((state: State) => state.auth.logoutLoading);
  const isDev = window.location.origin.includes('dev');
  const envAdmin = isDev ? 'admindev' : 'admin';

  const titleWidth = titleRef?.current?.offsetWidth || '50';
  const colors = ['#00E3AA', '#3434EF', '#D3FFA6', '#FF7442'];

  const [colorIndex, setColorIndex] = useState(0);
  const [widthAdjustment, setWidthAdjustment] = useState(0);

  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState('');

  const widthAdjustmentRef = useRef(widthAdjustment);
  widthAdjustmentRef.current = widthAdjustment;

  const getRandomWidthAdjustment = () => {
    const adjustments = [-20, 0, 20, 60];
    let newAdjustment = adjustments[Math.floor(Math.random() * adjustments.length)];

    // Ensure the new adjustment is different from the previous one
    while (newAdjustment === widthAdjustmentRef.current) {
      newAdjustment = adjustments[Math.floor(Math.random() * adjustments.length)];
    }

    return newAdjustment;
  };

  const currentWidth = titleRef?.current ? titleWidth + widthAdjustment : '255';

  const loginUser = async () => {
    setError(null);

    localStorage.setItem('internalUserId', userId);
    localStorage.setItem('internalOrgId', orgId);

    refreshLogin();
  };

  const refreshLogin = async (showError = true) => {
    const result = await dispatch(refresh());
    const from = location.state?.from || '/';

    if (result?.type === 'auth/refresh/rejected' && showError) {
      setError(
        'Failed to load user details for userId and orgId provided. Please check that both identifiers are valid using admin.telnyx.com.'
      );
    }

    if (result.type === 'auth/login/fulfilled' && !result?.payload?.error) {
      navigate(from, { replace: true });
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setColorIndex((prevIndex) => (prevIndex + 1) % colors.length);
      setWidthAdjustment(getRandomWidthAdjustment());
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!isLoggedIn && !loginLoading && !logoutLoading) refreshLogin(false);
  }, [location]);

  return (
    <Container>
      <Body>
        <Title>
          <Stack spacing={1} alignItems='center' justifyContent='center'>
            <h1 ref={titleRef}>Flow</h1>
            <Pill color={colors[colorIndex]} width={currentWidth.toString() || '400'} />
          </Stack>
          <p>Admin interface for impersonating users.</p>
        </Title>
        <InputContainer>
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              Failed to load user details for userId and orgId provided. Please check that both identifiers are valid
              using{' '}
              <a
                target='_blank'
                href={`https://${envAdmin}.telnyx.com/users/search`}
                rel='noreferrer'
              >{`${envAdmin}.telnyx.com`}</a>
              .
            </Alert>
          )}
          {!error && (
            <Alert severity='info'>
              Search for the users email at{' '}
              <a
                target='_blank'
                href={`https://${envAdmin}.telnyx.com/users/search`}
                rel='noreferrer'
              >{`${envAdmin}.telnyx.com`}</a>
              . Look for their User ID and Organisation ID. Enter them in the input fields below and click Login.
            </Alert>
          )}
          <TextField
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setError(null);
            }}
            variant='outlined'
            fullWidth
            placeholder='User ID'
          />
          <TextField
            value={orgId}
            onChange={(e) => {
              setOrgId(e.target.value);
              setError(null);
            }}
            variant='outlined'
            fullWidth
            placeholder='Organisation ID'
          />
          <LoadingButton
            fullWidth
            disabled={(autoLogin && isLocalUser) || invalidPermission || loginLoading}
            onClick={loginUser}
            loading={loginLoading}
            minHeight='40px'
          >
            Login
          </LoadingButton>
        </InputContainer>
      </Body>
    </Container>
  );
};
